import { type OfferTemplateRes, PurchaseContext } from '@on3/api/src';
import { useSubscribe } from '@on3/ui-lib/components/Subscription/Provider';
import { TheAthleticBarrier } from '@on3/ui-lib/components/Subscription/Templates/Barrier/TheAthletic';
import { Tracking } from '@on3/ui-lib/components/Tracking';
import { useAuth } from '@on3/ui-lib/contexts/AuthProvider';
import { type PurchaseModalProps } from '@on3/ui-lib/types/purchase';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';

import styles from './Barrier.module.scss';

export type ArticleBarrierVariant = 'article' | 'article-the-athletic';
export type ForumsBarrierVariant = 'forums' | 'forums-the-athletic';
export type BarrierVariant = ArticleBarrierVariant | ForumsBarrierVariant;

export interface BarrierProps {
  handleJoin: (props: PurchaseModalProps) => void;
  handleLogin: () => void;
  barrierType?: 'article' | 'forums';
  tmpVariant?: string;
  tmpTemplate?: OfferTemplateRes;
}

export type BarrierTemplateProps = Omit<BarrierProps, 'handleJoin'> & {
  handleJoin: (
    planKey: PurchaseModalProps['planKey'],
    couponKey?: PurchaseModalProps['couponKey'],
  ) => void;
  template: OfferTemplateRes;
  preview?: boolean;
};

const DefaultBarrier = ({
  handleJoin,
  handleLogin,
  template,
  preview = false,
}: BarrierTemplateProps) => {
  const { subscribeText } = useSubscribe();
  const { user } = useAuth();

  const title = template.data.title;
  const lineOne = template.data.line1;
  const lineTwo = template.data.line2;
  const buttonText =
    template.planData1?.buttonText || subscribeText || 'Join Now';
  const planKey = template.planKey1;

  return (
    <div
      className={clsx(styles.block, {
        [styles.preview]: preview,
      })}
      data-period="annual"
      data-template-type="barrier"
    >
      <div className={styles.container}>
        <h2>{title}</h2>
        <p className={styles.lineOne}>{lineOne}</p>
        <p className={styles.lineTwo}>{lineTwo}</p>
        <button
          className={styles.joinBtn}
          onClick={() => handleJoin(planKey)}
          type="button"
        >
          {buttonText}
        </button>
      </div>
      {!user?.uid && (
        <div className={styles.login}>
          <span>Already a subscriber?</span>
          <button onClick={handleLogin} type="button">
            Login
          </button>
        </div>
      )}
    </div>
  );
};

export const Barrier = ({
  handleJoin,
  handleLogin,
  barrierType = 'article',
  tmpTemplate,
  tmpVariant,
}: BarrierProps) => {
  const pathname = usePathname();
  const {
    isSubscribed,
    osKey,
    osSiteKey,
    templates: { [barrierType]: barrierTemplate },
  } = useSubscribe();

  const isTmpTemplate = Boolean(tmpTemplate);

  const template = tmpTemplate || barrierTemplate;
  const variant = (tmpVariant ||
    template?.key ||
    barrierType) as BarrierVariant;

  if (!template || (isSubscribed && !isTmpTemplate)) return null;

  const handleBarrierJoin = (
    planKey: PurchaseModalProps['planKey'],
    couponKey?: PurchaseModalProps['couponKey'],
  ) => {
    handleJoin({
      context:
        barrierType === 'article'
          ? PurchaseContext.Article
          : PurchaseContext.Forums,
      planKey,
      couponKey,
    });
  };

  const barrierProps = {
    handleJoin: handleBarrierJoin,
    handleLogin: pathname.startsWith('/protected')
      ? () => window.top?.postMessage({ type: 'REDIRECT_LOGIN' }, '*')
      : handleLogin,
    template,
    preview: Boolean(tmpVariant),
  };

  return (
    <Tracking
      data={{
        variant,
        offerSetKey: osKey,
        offerSetSiteKey: osSiteKey,
        creativeName: `${barrierType}_barrier`,
      }}
      disable={isTmpTemplate}
      event="view_promotion"
    >
      {variant === barrierType && <DefaultBarrier {...barrierProps} />}
      {variant === `${barrierType}-the-athletic` && (
        <TheAthleticBarrier {...barrierProps} />
      )}
    </Tracking>
  );
};
